import React, { useEffect, useRef, useState } from 'react';
import { ProductRegistrationModalProps, FormData } from './types';
import SimpleModal from '../../common/SimpleModal';
import ProgressIndicator from './ProgressIndicator';
import Step1BasicInfo from './Step1BasicInfo';
import Step4Complete from './Step4Complete';
import BannerExitConfirmation from './BannerExitConfirmation';
import UrlImageSelectionModal from '../UrlImageSelectionModal';
import KnowledgeFetchProgress from '../../../pages/knowledge/components/KnowledgeFetchProgress';
import SignupImageRoleStep from '../../onboarding/SignupImageRoleStep';
import SignupBannerGridStep from '../../onboarding/SignupBannerGridStep';
import productService from '../../../services/api/productService';
import productImageService, { ProductImage } from '../../../services/api/productImageService';
import signupOnboardingService from '../../../services/api/signupOnboardingService';
import { useToast } from '../../../contexts/ToastContext';
import onboardingV2Service from '../../../services/api/onboardingV2Service';
import CelebrationConfetti from '../../common/CelebrationConfetti';
import {
  buttonClass,
  BUTTON_GROUP,
  MODAL_CONTENT,
} from './styles';

type WizardStep = 1 | 2 | 3;
// 完了画面のキラキラCTAから始まる、モーダル内バナー生成サブフロー。null は通常ウィザード。
type BannerFlowStep = 'logo' | 'product-image' | 'grid';

const ProductRegistrationModal: React.FC<ProductRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onDetailedRegistration,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    url: '',
    tag_ids: [],
    category_id: null,
  });
  const [error, setError] = useState<string>('');
  const [product, setProduct] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [researchDone, setResearchDone] = useState(false);
  const [celebrationActive, setCelebrationActive] = useState(false);

  // 無料10枚バナー生成が使える組織か（完了画面のキラキラCTA・離脱確認の出し分けに使う）。
  const [freeBannerAvailable, setFreeBannerAvailable] = useState(false);
  // バナー生成サブフロー（ロゴ選択→商品画像選択→生成グリッド）。
  const [bannerFlowStep, setBannerFlowStep] = useState<BannerFlowStep | null>(null);
  const [roleImages, setRoleImages] = useState<ProductImage[]>([]);
  const [logoImageId, setLogoImageId] = useState<number | null>(null);
  const [productImageId, setProductImageId] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [bannerStarting, setBannerStarting] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  // Deep Research を二重に開始しないためのフラグ（Step3開始 と 途中で閉じた時の続行 の両方で使用）
  const researchStartedRef = useRef(false);

  const { showToast } = useToast();

  const trimmedUrl = formData.url.trim();

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setFormData({ name: '', url: '', tag_ids: [], category_id: null });
      setError('');
      setProduct(null);
      setIsSubmitting(false);
      setResearchDone(false);
      setCelebrationActive(false);
      researchStartedRef.current = false;
      // バナー生成サブフロー関連の状態も初期化する。
      setFreeBannerAvailable(false);
      setBannerFlowStep(null);
      setRoleImages([]);
      setLogoImageId(null);
      setProductImageId(null);
      setSessionId(null);
      setBannerStarting(false);
      setBannerError('');
      setExitConfirmOpen(false);
    }
  }, [isOpen]);

  // モーダルを開いたら eligibility を取得しておく（完了画面までに判定を用意する）。
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    onboardingV2Service
      .getProgress()
      .then((progress) => {
        if (!cancelled) setFreeBannerAvailable(progress.free_banner_available ?? false);
      })
      .catch(() => {
        if (!cancelled) setFreeBannerAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleChange = (field: 'name' | 'url' | 'tag_ids' | 'category_id', value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  // Step1: URLで商品を作成（Deep Research は遅延して開始しない）。
  const handleCreateProduct = async () => {
    if (!trimmedUrl) {
      setError('URLを入力してください');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await productService.createFromUrl({
        name: '',
        description: '',
        url: trimmedUrl,
        tag_ids: formData.tag_ids,
        category_id: formData.category_id,
        start_fetch: false,
      });

      if (!response.data) {
        setError('商品の登録に失敗しました');
        return;
      }

      setProduct(response.data);
      setCurrentStep(2);
    } catch (error: any) {
      setError(
        error?.response?.data?.message || error?.message || '商品の登録中にエラーが発生しました'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // 遅延している Deep Research を開始する（多重開始を防止）。
  const startResearch = async () => {
    if (!product?.id || researchStartedRef.current) return;
    researchStartedRef.current = true;
    await productService.startProductResearch(product.id);
  };

  // Step2 → Step3: 競合調査は行わず、商品情報の生成だけを開始する。
  const handleStartResearch = async () => {
    if (!product?.id) return;
    setError('');
    try {
      await startResearch();
      showToast('商品の登録を開始しました');
      onSuccess?.(product.id);
      setCurrentStep(3);
    } catch (error: any) {
      setError(error?.response?.data?.message || error?.message || '情報の取得開始に失敗しました');
      researchStartedRef.current = false;
    }
  };

  // 画像選択（Step2）の完了/スキップ → Deep Research（Step3）へ。
  const handleImageStepDone = () => {
    void handleStartResearch();
  };

  // モーダルを閉じる。商品作成後・Deep Research 未開始なら、その時点の選択で取得を開始して続行する。
  const handleClose = () => {
    if (product?.id && !researchStartedRef.current) {
      startResearch().catch(() => {});
      showToast('商品の登録を開始しました');
      onSuccess?.(product.id);
    }
    onClose();
  };

  // 無料バナーを作れる状態で「進捗中／完了画面／画像選択」を閉じる時は確認を挟む。
  // グリッド表示中・Step1/Step2・非対象組織は確認なしで従来通り閉じる。
  const shouldConfirmExit = (): boolean => {
    if (!freeBannerAvailable) return false;
    if (bannerFlowStep === 'grid') return false;
    if (bannerFlowStep === 'logo' || bannerFlowStep === 'product-image') return true;
    return currentStep === 3;
  };

  const requestClose = () => {
    if (shouldConfirmExit()) {
      setExitConfirmOpen(true);
      return;
    }
    handleClose();
  };

  const handleResearchDone = async () => {
    setResearchDone(true);
    if (product?.id) {
      onSuccess?.(product.id);
    }

    try {
      const response = await onboardingV2Service.claim();
      // claim レスポンスの最新 progress からも eligibility を更新する。
      setFreeBannerAvailable(response.progress.free_banner_available ?? false);
      if (response.granted_now.product) {
        setCelebrationActive(true);
      }
    } catch (error) {
      console.error('Failed to claim onboarding product bonus:', error);
    }
  };

  // 完了画面のキラキラCTA → 商品画像を読み込み、ロゴ選択ステップへ。
  const handleStartBannerFlow = async () => {
    if (!product?.id || bannerStarting) return;
    setBannerStarting(true);
    setBannerError('');
    try {
      const res = await productImageService.getProductImages(product.id);
      const imgs = res.data || [];
      setRoleImages(imgs);
      setLogoImageId(imgs.find((img) => img.category === 'brand_logo')?.id ?? null);
      setProductImageId(imgs.find((img) => img.category === 'product_image')?.id ?? null);
      setBannerFlowStep('logo');
    } catch {
      setBannerError('画像の読み込みに失敗しました。もう一度お試しください。');
    } finally {
      setBannerStarting(false);
    }
  };

  // 画像の役割を保存し（バックエンドが参照する）、バナー生成を開始してグリッドへ。
  // Deep Research は既に完了しているため起動しない。
  const handlePersistRolesAndGenerate = async () => {
    if (!product?.id || bannerStarting) return;
    setBannerStarting(true);
    setBannerError('');
    try {
      if (productImageId) {
        await productImageService.setPrimaryImage(product.id, productImageId);
      }
      const detail = await productService.getKnowledgeProductById(product.id);
      const metadata = { ...(detail.data.extended_metadata || {}) };
      metadata.onboarding_assets = {
        logo_image_id: logoImageId,
        product_image_id: productImageId,
      };
      await productService.updateKnowledgeProduct(product.id, { extended_metadata: metadata });

      const generation = await signupOnboardingService.generateBanners(product.id);
      setSessionId(generation.session_id);
      setBannerFlowStep('grid');
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // 既に生成済み。エラー表示ではなく通常の完了画面へ戻す。
        setFreeBannerAvailable(false);
        setBannerFlowStep(null);
        return;
      }
      setBannerError('バナー生成を開始できませんでした。もう一度お試しください。');
    } finally {
      setBannerStarting(false);
    }
  };

  const handleDetailedRegistration = () => {
    onClose();
    onDetailedRegistration?.();
  };

  const handleViewProducts = () => {
    if (product?.id) {
      onSuccess?.(product.id);
    }
    onClose();
  };

  const getStepTitle = (): string => {
    if (bannerFlowStep === 'logo') return 'ロゴを選択';
    if (bannerFlowStep === 'product-image') return '商品画像を選択';
    if (bannerFlowStep === 'grid') return '無料バナーを生成';
    switch (currentStep) {
      case 1:
        return '商品情報を登録';
      case 2:
        return '画像を選択';
      case 3:
        return researchDone ? '登録完了' : 'Deep Research';
      default:
        return '';
    }
  };

  const renderResearchStep = () => {
    if (researchDone) {
      return (
        <Step4Complete
          product={product}
          onViewProducts={handleViewProducts}
          onClose={requestClose}
          freeBannerAvailable={freeBannerAvailable}
          onStartBanners={handleStartBannerFlow}
          startingBanners={bannerStarting}
        />
      );
    }
    return (
      <div className={MODAL_CONTENT}>
        {product?.id && (
          <KnowledgeFetchProgress
            productId={product.id}
            variant="modal"
            onDone={handleResearchDone}
            enabled={currentStep === 3}
          />
        )}
        <div className="mt-4 text-center text-[12px] leading-relaxed text-gray-500">
          閉じても取得は続きます。
        </div>
        <div className={BUTTON_GROUP}>
          <button className={buttonClass('outline')} type="button" onClick={requestClose}>
            閉じる
          </button>
        </div>
      </div>
    );
  };

  // キラキラCTA後のバナー生成サブフロー（ロゴ選択 → 商品画像選択 → 生成グリッド）。
  const renderBannerFlow = () => {
    if (bannerFlowStep === 'grid' && sessionId) {
      // 生成はバックグラウンドで継続し /ads/banner の履歴に載る。閉じても止まらない。
      return (
        <div className={MODAL_CONTENT}>
          <SignupBannerGridStep
            sessionId={sessionId}
            onContinue={handleViewProducts}
            onSkip={handleViewProducts}
          />
        </div>
      );
    }

    if (bannerFlowStep === 'logo' || bannerFlowStep === 'product-image') {
      const role = bannerFlowStep === 'logo' ? 'logo' : 'product';
      const imagesForStep =
        role === 'logo' ? roleImages : roleImages.filter((img) => img.id !== logoImageId);
      const selectedId =
        role === 'logo'
          ? logoImageId
          : productImageId === logoImageId
            ? null
            : productImageId;

      return (
        <div className={MODAL_CONTENT}>
          {bannerError && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-600">
              {bannerError}
            </div>
          )}
          <SignupImageRoleStep
            role={role}
            images={imagesForStep}
            selectedId={selectedId}
            productId={product.id}
            onSelect={role === 'logo' ? setLogoImageId : setProductImageId}
            onUploaded={(image) => {
              setRoleImages((prev) => [...prev, image]);
              if (role === 'logo') {
                setLogoImageId(image.id);
              } else {
                setProductImageId(image.id);
              }
            }}
            onBack={role === 'logo' ? () => setBannerFlowStep(null) : () => setBannerFlowStep('logo')}
            onNext={
              role === 'logo'
                ? () => setBannerFlowStep('product-image')
                : handlePersistRolesAndGenerate
            }
            nextLoading={role === 'product' && bannerStarting}
            nextLoadingMessage="無料バナーの生成を準備しています。画面が切り替わるまでそのままお待ちください。"
          />
        </div>
      );
    }

    return null;
  };

  // Step2（画像選択）は既存の UrlImageSelectionModal をそのまま流用する。
  if (isOpen && currentStep === 2 && product?.id) {
    return (
      <UrlImageSelectionModal
        isOpen={isOpen}
        url={formData.url}
        productId={product.id}
        onComplete={handleImageStepDone}
        onSkip={handleImageStepDone}
        onClose={handleClose}
      />
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            formData={formData}
            onChange={handleChange}
            onNext={handleCreateProduct}
            onCancel={onClose}
            onDetailedRegistration={
              onDetailedRegistration ? handleDetailedRegistration : undefined
            }
            error={error}
            isSubmitting={isSubmitting}
          />
        );
      case 3:
        return renderResearchStep();
      default:
        return null;
    }
  };

  const renderBody = () => (bannerFlowStep ? renderBannerFlow() : renderStep());

  return (
    <>
      <SimpleModal
        isOpen={isOpen}
        onClose={requestClose}
        title={getStepTitle()}
        size={bannerFlowStep === 'grid' ? 'xl' : 'lg'}
        className="product-registration-modal"
      >
        {!researchDone && !bannerFlowStep && (
          <ProgressIndicator currentStep={currentStep} totalSteps={3} />
        )}
        {renderBody()}
      </SimpleModal>
      <BannerExitConfirmation
        open={exitConfirmOpen}
        onLeave={() => {
          setExitConfirmOpen(false);
          handleClose();
        }}
        onStay={() => setExitConfirmOpen(false)}
      />
      <CelebrationConfetti
        active={celebrationActive}
        onComplete={() => setCelebrationActive(false)}
      />
    </>
  );
};

export default ProductRegistrationModal;
